import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { SynthAudio } from './audio/SynthAudio'
import { BALANCE, COLORS } from './config'
import { EnemyUnit } from './entities/EnemyUnit'
import { PlayerMech } from './entities/PlayerMech'
import {
  STAGGER_BREAK_STOP_SECONDS,
  accumulateDamage,
  fovKickDegrees,
  hitTier,
  incomingPulse,
  incomingShake,
  killHitStopSeconds,
  weaponHitStopSeconds,
  type DamageCounterState,
  type HitTier,
  type HitWeapon,
} from './feedback'
import { InputManager } from './input/InputManager'
import { chooseLockTarget, clamp, evaluateMission } from './mechanics'
import { MissionDirector, type MissionWaveDefinition } from './mission/MissionDirector'
import { summarizeFrameTimes, type FrameTimeSummary } from './performance'
import { Effects } from './render/Effects'
import { HostileProjectileBatch } from './render/HostileProjectileBatch'
import { HUD, type HUDState } from './ui/HUD'
import { Arena } from './world/Arena'

type GamePhase = 'briefing' | 'playing' | 'paused' | 'victory' | 'defeat'

interface HostileProjectile {
  position: THREE.Vector3
  velocity: THREE.Vector3
  life: number
  damageScale: number
}

interface GuidedMissile {
  group: THREE.Group
  velocity: THREE.Vector3
  targetId: string
  life: number
}

/** Snapshot record of the most recent scoring hit on an enemy. */
export interface LastHitRecord {
  weapon: HitWeapon
  damage: number
  direct: boolean
  tier: HitTier
  atSeconds: number
}

/** Snapshot record of the most recent hit the player took. */
export interface LastPlayerHitRecord {
  damage: number
  direct: boolean
  shake: number
  atSeconds: number
}

export interface GameSnapshot {
  ready: boolean
  phase: GamePhase
  elapsedSeconds: number
  score: number
  player: {
    x: number
    y: number
    z: number
    armor: number
    energy: number
    stagger: number
    speed: number
    collided: boolean
  }
  targetId: string | null
  targetVisible: boolean
  targetArmor: number | null
  targetStagger: number | null
  targetPhase: 1 | 2 | null
  assaultBoosting: boolean
  lockProgress: number
  enemiesAlive: number
  rifleAmmo: number
  missiles: number
  rifleHits: number
  missileHits: number
  averageFps: number
  /** Wiring evidence for the feedback pass: what the last landed hit WAS. */
  lastHit: LastHitRecord | null
  lastPlayerHit: LastPlayerHitRecord | null
  targetOverloaded: boolean | null
  /** Cumulative count of simulation freezes fired by heavy hits. */
  hitStopsTriggered: number
  /** True while a capture freeze (test mode) is holding the simulation. */
  fxFrozen: boolean
  camera: {
    x: number
    y: number
    z: number
    distance: number
    nominalDistance: number
    obstructed: boolean
  }
  spatialObstacles: number
  performance: FrameTimeSummary & {
    bootDurationMs: number
    drawCalls: number
    triangles: number
    geometries: number
    textures: number
  }
  mission: {
    phase: 'active' | 'intermission' | 'complete'
    stage: number
    totalStages: number
    waveEnemiesAlive: number
    intermissionRemaining: number
    progress: number
  }
}

/** What a capture trap waits for. Armed via `armCaptureTrapForTest`. */
export type CaptureTrapKind = 'hit' | 'direct' | 'kill' | 'playerHit' | 'staggerBreak'

export interface GameTestBridge {
  readonly ready: boolean
  start(): void
  snapshot(): GameSnapshot
  defeatActiveWaveForTest?(armorFraction?: number): void
  setSpatialStateForTest?(state: SpatialTestState): void
  measureFxBudgetsForTest?(): FxBudgetMeasurement
  measureRenderCostForTest?(frames?: number): Promise<RenderCostMeasurement>
  damagePlayerForTest?(damage: number, impact?: number): void
  overloadTargetForTest?(): void
  freezeFxForTest?(frozen: boolean): void
  armCaptureTrapForTest?(kind: CaptureTrapKind, weapon?: HitWeapon): void
}

interface DrawCallMeasurement {
  effects: number
  beforeCalls: number
  afterCalls: number
  addedDrawCalls: number
  beforeGeometries: number
  afterGeometries: number
  addedGeometries: number
  beforeTriangles: number
  afterTriangles: number
  addedTriangles: number
}

export interface FxBudgetMeasurement {
  dust: DrawCallMeasurement
  impact: DrawCallMeasurement
  hostileProjectile: DrawCallMeasurement
  /** Rifle scoring-hit effect (sparks + ring), 4 events. New in the feedback
   * pass; `impact` above is unchanged and keeps its historical parity. */
  rifleHit: DrawCallMeasurement
  /** Blade direct-hit effect (arc + cut + sparks + flash + ring), 1 event. */
  bladeHit: DrawCallMeasurement
}

export interface RenderCostMeasurement {
  /** Unmasked GPU string, so a recorded number names the hardware it came from. */
  renderer: string
  /**
   * How the numbers were obtained.
   * - `gpu-timer-query`: real GPU elapsed time from `EXT_disjoint_timer_query_webgl2`.
   * - `cpu-submission`: CPU time to submit the frame. NOT a frame time — the
   *   driver is free to return before the GPU has done the work.
   */
  method: 'gpu-timer-query' | 'cpu-submission'
  /** True when the driver reported a disjoint event and the timings are void. */
  disjoint: boolean
  frames: number
  drawCalls: number
  triangles: number
  pixelRatio: number
  backingPixels: number
  viewport: { width: number; height: number }
  cost: FrameTimeSummary
}

export interface SpatialTestState {
  player: readonly [number, number, number]
  enemies: ReadonlyArray<readonly [number, number, number]>
  cameraYaw?: number
}

export class Game {
  private readonly scene = new THREE.Scene()
  private readonly camera = new THREE.PerspectiveCamera(58, 1, 0.1, 520)
  private readonly renderer: THREE.WebGLRenderer
  private readonly composer: EffectComposer
  private readonly input: InputManager
  private readonly arena: Arena
  private readonly player: PlayerMech
  private readonly effects: Effects
  private readonly hostileProjectileBatch = new HostileProjectileBatch(this.scene)
  private readonly hud = new HUD()
  private readonly audio = new SynthAudio()
  private readonly mission = new MissionDirector()
  private readonly enemies: EnemyUnit[] = []
  private readonly hostileProjectiles: HostileProjectile[] = []
  private readonly missiles: GuidedMissile[] = []
  private readonly frameTimes: number[] = []
  private phase: GamePhase = 'briefing'
  private targetId: string | null = null
  private lockProgress = 0
  private rifleCooldown = 0
  private missileCooldown = 0
  private bladeCooldown = 0
  private reloadTimer = 0
  private rifleAmmo = 36
  private rifleReserve = 180
  private missileAmmo = 16
  private rifleHits = 0
  private missileHits = 0
  private elapsedSeconds = 0
  private score = 0
  private cameraYaw = 0
  private cameraPitch = 0.23
  private cameraShake = 0
  private cameraObstructed = false
  private targetVisible = false
  private pointerWasCaptured = false
  private currentSpeed = 0
  private boosting = false
  private assaultBoosting = false
  private dustTimer = 0
  private accumulator = 0
  private previousTimestamp = performance.now()
  private readonly bootStartedAt = performance.now()
  private bootDurationMs = 0
  private disposed = false
  private animationFrame = 0
  /** Simulation freeze remaining, in real seconds. Rendering continues. */
  private hitStopRemaining = 0
  private hitStopsTriggered = 0
  /** Test-capture freeze: like a hit-stop with no timer. */
  private fxFrozen = false
  /** Degrees subtracted from the target FOV; decays fast. A landed blade
   * tightens the frame for an instant — the camera answering the hit. */
  private fovKick = 0
  private lastHit: LastHitRecord | null = null
  private lastPlayerHit: LastPlayerHitRecord | null = null
  /** Armed capture trap: freezes FX the very tick the matching event fires.
   * Exists because arming a freeze from OUTSIDE the page races the renderer —
   * on a slow machine the moment is gone before the freeze arrives. */
  private captureTrap: { kind: CaptureTrapKind; weapon?: HitWeapon } | null = null

  constructor(private readonly canvas: HTMLCanvasElement, hudContainer: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    })
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    // Lifted alongside the bloom pull-back below: the old exposure leaned on
    // bloom to carry brightness, which is what blew out the mech's chest.
    this.renderer.toneMappingExposure = 1.0
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.65))
    this.renderer.info.autoReset = false

    // Without an environment map, `metalness` means "reflect nothing", i.e.
    // black — which is why the old build needed heavy bloom to be legible at
    // all. One prefiltered environment gives every metal surface something to
    // reflect, and it is generated once at boot.
    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
    this.scene.environmentIntensity = 0.55
    pmrem.dispose()

    this.scene.add(this.camera)
    const cameraFill = new THREE.DirectionalLight(0xb9eaff, 1.35)
    cameraFill.position.set(2, 4, 3)
    cameraFill.target.position.set(0, 0, -12)
    this.camera.add(cameraFill, cameraFill.target)

    this.composer = new EffectComposer(this.renderer)
    this.composer.addPass(new RenderPass(this.scene, this.camera))
    // strength 0.72 -> 0.44, threshold 0.74 -> 0.95. The old settings bloomed
    // lit armour, not just emitters, which erased the mech's own forms. The
    // true emitters (thrusters, tracers, muzzle flashes) are `toneMapped:
    // false` and exceed any threshold, so they keep blooming; raising it only
    // stops sunlit plate from smearing.
    this.composer.addPass(
      new UnrealBloomPass(new THREE.Vector2(1, 1), 0.44, 0.46, 0.95),
    )
    this.composer.addPass(new OutputPass())

    this.input = new InputManager(canvas)
    this.arena = new Arena(this.scene)
    this.effects = new Effects(this.scene)
    this.player = new PlayerMech(this.scene, this.arena)
    this.spawnWave(this.mission.currentWave)

    this.hud.mount(hudContainer)
    this.hud.showStart(() => this.start())
    this.updateHud()
    this.resize()
    window.addEventListener('resize', this.resize)
    window.addEventListener('beforeunload', this.dispose, { once: true })
    canvas.addEventListener('click', this.capturePointer)
    document.addEventListener('pointerlockchange', this.handlePointerLockChange)
    window.addEventListener('keydown', this.handleGlobalKeyDown)
    document.body.dataset.gameReady = 'true'
    this.bootDurationMs = performance.now() - this.bootStartedAt
    this.animationFrame = requestAnimationFrame(this.frame)
  }

  start(): void {
    if (this.phase !== 'briefing') return
    this.phase = 'playing'
    this.elapsedSeconds = 0
    this.frameTimes.length = 0
    this.previousTimestamp = performance.now()
    this.accumulator = 0
    this.hud.hideStart()
    this.hud.setToast('COMBAT LINK ESTABLISHED', 'success', 1800)
    this.input.clearTransient()
    void this.audio.start()
    this.input.requestPointerLock()
    this.acquireTarget()
  }

  pause(): void {
    if (this.phase !== 'playing') return
    this.phase = 'paused'
    this.boosting = false
    this.assaultBoosting = false
    this.hud.setBoost(false, false)
    this.input.resetAll()
    this.audio.updateEngine(0, false)
    this.hud.showPause(() => this.resume())
    if (document.pointerLockElement === this.canvas) void document.exitPointerLock()
  }

  resume(): void {
    if (this.phase !== 'paused') return
    this.phase = 'playing'
    this.previousTimestamp = performance.now()
    this.accumulator = 0
    this.input.resetAll()
    this.hud.hidePause()
    void this.audio.start()
    this.input.requestPointerLock()
  }

  snapshot(): GameSnapshot {
    const target = this.currentTarget()
    const enemiesAlive = this.enemies.filter((enemy) => enemy.alive).length
    const performanceSummary = summarizeFrameTimes(this.frameTimes)
    return {
      ready: true,
      phase: this.phase,
      elapsedSeconds: Number(this.elapsedSeconds.toFixed(2)),
      score: this.score,
      player: {
        x: Number(this.player.position.x.toFixed(3)),
        y: Number(this.player.position.y.toFixed(3)),
        z: Number(this.player.position.z.toFixed(3)),
        armor: Math.round(this.player.armor),
        energy: Number(this.player.energy.value.toFixed(2)),
        stagger: Number(this.player.stagger.value.toFixed(2)),
        speed: Number(this.currentSpeed.toFixed(2)),
        collided: this.player.collided,
      },
      targetId: this.targetId,
      targetVisible: this.targetVisible,
      targetArmor: target ? Math.round(target.armor) : null,
      targetStagger: target ? Number(target.stagger.value.toFixed(2)) : null,
      targetPhase: target ? target.combatPhase : null,
      assaultBoosting: this.assaultBoosting,
      lockProgress: Number(this.lockProgress.toFixed(3)),
      enemiesAlive,
      rifleAmmo: this.rifleAmmo,
      missiles: this.missileAmmo,
      rifleHits: this.rifleHits,
      missileHits: this.missileHits,
      averageFps: performanceSummary.averageFps,
      lastHit: this.lastHit ? { ...this.lastHit } : null,
      lastPlayerHit: this.lastPlayerHit ? { ...this.lastPlayerHit } : null,
      targetOverloaded: target ? target.stagger.overloadedFor > 0 : null,
      hitStopsTriggered: this.hitStopsTriggered,
      fxFrozen: this.fxFrozen,
      camera: {
        x: Number(this.camera.position.x.toFixed(3)),
        y: Number(this.camera.position.y.toFixed(3)),
        z: Number(this.camera.position.z.toFixed(3)),
        distance: Number(this.camera.position.distanceTo(this.player.aimPoint).toFixed(3)),
        nominalDistance: this.nominalCameraDistance(),
        obstructed: this.cameraObstructed,
      },
      spatialObstacles: this.arena.obstacles.length,
      performance: {
        ...performanceSummary,
        bootDurationMs: Number(this.bootDurationMs.toFixed(2)),
        drawCalls: this.renderer.info.render.calls,
        triangles: this.renderer.info.render.triangles,
        geometries: this.renderer.info.memory.geometries,
        textures: this.renderer.info.memory.textures,
      },
      mission: this.mission.snapshot(enemiesAlive),
    }
  }

  /**
   * @param armorFraction Portion of each live enemy's maximum armour to remove.
   *   Defaults to overkill so callers that just want the wave gone stay simple.
   *   Pass a fraction to walk a command unit up to its phase threshold without
   *   destroying it, which the one-shot form cannot do.
   */
  defeatActiveWaveForTest(armorFraction = 10): void {
    if (this.phase !== 'playing') return
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue
      this.damageEnemy(enemy, enemy.maximumArmor * armorFraction, 0, enemy.aimPoint)
    }
  }

  /**
   * Cost of drawing the current scene, measured without `requestAnimationFrame`.
   *
   * rAF is unusable for this: it is capped by vsync, and it stops entirely when
   * the page is not visible, which is the state an automated browser leaves it
   * in — the loop never ticks and the sample comes back empty.
   *
   * Timing is taken from `EXT_disjoint_timer_query_webgl2`, which reports the
   * GPU's own elapsed time. The obvious alternative, `performance.now()` around
   * a `render()` plus `gl.finish()`, does NOT work here: on ANGLE/Metal the
   * finish returns before the GPU has drained, and the resulting figure *fell*
   * when the backing store was enlarged from 2.5 to 5.7 megapixels — it was
   * timing command submission, not drawing. `cpu-submission` is reported only
   * when the extension is missing, and is labelled so it cannot be mistaken for
   * a frame time.
   *
   * The simulation step is not included. This is draw cost alone.
   */
  async measureRenderCostForTest(frames = 120): Promise<RenderCostMeasurement> {
    const gl = this.renderer.getContext() as WebGL2RenderingContext
    const step = BALANCE.fixedStep
    const timer = gl.getExtension('EXT_disjoint_timer_query_webgl2') as
      | { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number }
      | null

    // Warm-up: the first renders pay shader compilation and buffer upload.
    for (let index = 0; index < 12; index += 1) this.composer.render(step)
    gl.finish()

    const seconds: number[] = []
    let disjoint = false

    if (timer) {
      const queries: WebGLQuery[] = []
      for (let index = 0; index < frames; index += 1) {
        const query = gl.createQuery()
        if (!query) break
        gl.beginQuery(timer.TIME_ELAPSED_EXT, query)
        this.composer.render(step)
        gl.endQuery(timer.TIME_ELAPSED_EXT)
        queries.push(query)
      }
      // Timer results are not available synchronously, and `gl.finish()` does
      // not make them so — reading straight after it yields zero samples. Poll
      // on the macrotask queue instead of rAF, which is frozen whenever the
      // page is not visible.
      gl.finish()
      const last = queries[queries.length - 1]
      for (let attempt = 0; last && attempt < 200; attempt += 1) {
        if (gl.getQueryParameter(last, gl.QUERY_RESULT_AVAILABLE)) break
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
      disjoint = Boolean(gl.getParameter(timer.GPU_DISJOINT_EXT))
      for (const query of queries) {
        if (gl.getQueryParameter(query, gl.QUERY_RESULT_AVAILABLE)) {
          // Nanoseconds.
          seconds.push(Number(gl.getQueryParameter(query, gl.QUERY_RESULT)) / 1e9)
        }
        gl.deleteQuery(query)
      }
    } else {
      for (let index = 0; index < frames; index += 1) {
        const startedAt = performance.now()
        this.composer.render(step)
        gl.finish()
        seconds.push((performance.now() - startedAt) / 1_000)
      }
    }

    const debug = gl.getExtension('WEBGL_debug_renderer_info')
    const size = this.renderer.getSize(new THREE.Vector2())
    const ratio = this.renderer.getPixelRatio()
    this.renderer.info.reset()
    this.composer.render(step)
    return {
      renderer: debug
        ? String(gl.getParameter(debug.UNMASKED_RENDERER_WEBGL))
        : String(gl.getParameter(gl.RENDERER)),
      method: timer ? 'gpu-timer-query' : 'cpu-submission',
      disjoint,
      frames,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      pixelRatio: ratio,
      backingPixels: Math.round(size.x * ratio) * Math.round(size.y * ratio),
      viewport: { width: size.x, height: size.y },
      cost: summarizeFrameTimes(seconds),
    }
  }

  /**
   * Deterministic incoming damage, for the defeat-path E2E and the "moment of
   * being hit" capture — states the old harness could never reach on purpose.
   * Runs through the exact production pipeline (applyDamage → feedback), so
   * what it photographs is what a real hit produces.
   */
  damagePlayerForTest(damage: number, impact = 9): void {
    if (this.phase !== 'playing') return
    const wasOverloaded = this.player.stagger.overloadedFor > 0
    const source = this.player.position.clone().add(new THREE.Vector3(6, 4, -10))
    const outcome = this.player.applyDamage(damage, impact, source)
    const applied = wasOverloaded ? damage * 1.45 : damage
    const heavy = applied >= 500
    const shake = incomingShake(applied)
    this.effects.impact(this.player.aimPoint, true)
    if (heavy) this.effects.hit({ weapon: 'missile', tier: 'solid', direct: false }, this.player.aimPoint)
    this.audio.playerHit(heavy, wasOverloaded)
    this.hud.pulseDamage(incomingPulse(applied))
    this.hud.markHitDirection(this.player.lastHitYaw)
    this.hud.showIncomingDamage(Math.floor(applied), wasOverloaded)
    this.cameraShake = Math.max(this.cameraShake, shake)
    this.lastPlayerHit = {
      damage: Math.floor(applied),
      direct: wasOverloaded,
      shake: Number(shake.toFixed(3)),
      atSeconds: Number(this.elapsedSeconds.toFixed(2)),
    }
    this.springCaptureTrap('playerHit')
    if (outcome.overloaded) {
      this.effects.staggerBreak(this.player.aimPoint)
      this.audio.staggerBreak()
      this.hud.setToast('ACS OVERLOAD // DIRECT HIT RISK', 'warning', 1700)
    }
  }

  /**
   * Push the current target's ACS to its threshold through the production hit
   * pipeline (damage 0, impact = its full gauge), so the stagger-break moment
   * can be reached and photographed deterministically.
   */
  overloadTargetForTest(): void {
    if (this.phase !== 'playing') return
    const target = this.currentTarget() ?? this.enemies.find((enemy) => enemy.alive) ?? null
    if (!target) return
    if (!this.targetId) {
      this.targetId = target.id
      this.targetVisible = true
    }
    this.damageEnemy(target, 0, target.maximumStagger, target.aimPoint, 'system')
  }

  /** Freeze/thaw simulation and HUD animation for pixel-stable captures. */
  freezeFxForTest(frozen: boolean): void {
    this.fxFrozen = frozen
    this.hud.setFrozen(frozen)
    if (!frozen) {
      this.previousTimestamp = performance.now()
      this.accumulator = 0
    }
  }

  /** Arm a one-shot trap: the next matching feedback event freezes FX on the
   * same simulation tick it happens, so the capture shows frame zero. */
  armCaptureTrapForTest(kind: CaptureTrapKind, weapon?: HitWeapon): void {
    this.captureTrap = { kind, ...(weapon ? { weapon } : {}) }
  }

  private springCaptureTrap(kind: CaptureTrapKind, weapon?: HitWeapon): void {
    const trap = this.captureTrap
    if (!trap || trap.kind !== kind) return
    if (trap.weapon && trap.weapon !== weapon) return
    this.captureTrap = null
    this.freezeFxForTest(true)
  }

  setSpatialStateForTest(state: SpatialTestState): void {
    this.player.position.set(...state.player)
    this.player.velocity.set(0, 0, 0)
    this.player.rig.root.position.copy(this.player.position)
    state.enemies.forEach((position, index) => {
      const enemy = this.enemies[index]
      if (!enemy) return
      enemy.position.set(...position)
      enemy.velocity.set(0, 0, 0)
    })
    this.cameraYaw = state.cameraYaw ?? this.cameraYaw
    this.targetId = null
    this.lockProgress = 0
    this.targetVisible = false
    this.acquireTarget()
  }

  measureFxBudgetsForTest(): FxBudgetMeasurement {
    if (this.phase !== 'briefing') throw new Error('FX budgets require the briefing phase')
    this.renderer.info.reset()
    this.composer.render(0)
    const beforeDustCalls = this.renderer.info.render.calls
    const beforeDustGeometries = this.renderer.info.memory.geometries
    const beforeDustTriangles = this.renderer.info.render.triangles
    const origin = this.player.position.clone()
    const drift = new THREE.Vector3()
    const dustEffects = this.effects.poolStats().dustCapacity
    for (let index = 0; index < dustEffects; index += 1) {
      this.effects.dust(origin, drift, 1)
    }
    this.renderer.info.reset()
    this.composer.render(0)
    const afterDustCalls = this.renderer.info.render.calls
    const afterDustGeometries = this.renderer.info.memory.geometries
    const afterDustTriangles = this.renderer.info.render.triangles
    const impactEffects = 4
    const impactOrigin = this.player.aimPoint.clone()
    for (let index = 0; index < impactEffects; index += 1) this.effects.impact(impactOrigin)
    this.renderer.info.reset()
    this.composer.render(0)
    const afterImpactCalls = this.renderer.info.render.calls
    const afterImpactGeometries = this.renderer.info.memory.geometries
    const afterImpactTriangles = this.renderer.info.render.triangles
    const hostileProjectileEffects = 4
    const hostileProjectileStates = Array.from({ length: hostileProjectileEffects }, (_, index) => ({
      position: impactOrigin.clone().add(new THREE.Vector3((index - 1.5) * 0.55, 0.8, 0)),
    }))
    this.hostileProjectileBatch.sync(hostileProjectileStates)
    this.renderer.info.reset()
    this.composer.render(0)
    const afterHostileProjectileCalls = this.renderer.info.render.calls
    const afterHostileProjectileGeometries = this.renderer.info.memory.geometries
    const afterHostileProjectileTriangles = this.renderer.info.render.triangles
    const rifleHitEffects = 4
    for (let index = 0; index < rifleHitEffects; index += 1) {
      this.effects.hit({ weapon: 'rifle', tier: 'graze', direct: false }, impactOrigin)
    }
    this.renderer.info.reset()
    this.composer.render(0)
    const afterRifleHitCalls = this.renderer.info.render.calls
    const afterRifleHitGeometries = this.renderer.info.memory.geometries
    const afterRifleHitTriangles = this.renderer.info.render.triangles
    this.effects.hit({ weapon: 'blade', tier: 'severe', direct: true }, impactOrigin)
    this.renderer.info.reset()
    this.composer.render(0)
    const afterBladeHitCalls = this.renderer.info.render.calls
    const afterBladeHitGeometries = this.renderer.info.memory.geometries
    const afterBladeHitTriangles = this.renderer.info.render.triangles
    return {
      dust: {
        effects: dustEffects,
        beforeCalls: beforeDustCalls,
        afterCalls: afterDustCalls,
        addedDrawCalls: afterDustCalls - beforeDustCalls,
        beforeGeometries: beforeDustGeometries,
        afterGeometries: afterDustGeometries,
        addedGeometries: afterDustGeometries - beforeDustGeometries,
        beforeTriangles: beforeDustTriangles,
        afterTriangles: afterDustTriangles,
        addedTriangles: afterDustTriangles - beforeDustTriangles,
      },
      impact: {
        effects: impactEffects,
        beforeCalls: afterDustCalls,
        afterCalls: afterImpactCalls,
        addedDrawCalls: afterImpactCalls - afterDustCalls,
        beforeGeometries: afterDustGeometries,
        afterGeometries: afterImpactGeometries,
        addedGeometries: afterImpactGeometries - afterDustGeometries,
        beforeTriangles: afterDustTriangles,
        afterTriangles: afterImpactTriangles,
        addedTriangles: afterImpactTriangles - afterDustTriangles,
      },
      hostileProjectile: {
        effects: hostileProjectileEffects,
        beforeCalls: afterImpactCalls,
        afterCalls: afterHostileProjectileCalls,
        addedDrawCalls: afterHostileProjectileCalls - afterImpactCalls,
        beforeGeometries: afterImpactGeometries,
        afterGeometries: afterHostileProjectileGeometries,
        addedGeometries: afterHostileProjectileGeometries - afterImpactGeometries,
        beforeTriangles: afterImpactTriangles,
        afterTriangles: afterHostileProjectileTriangles,
        addedTriangles: afterHostileProjectileTriangles - afterImpactTriangles,
      },
      rifleHit: {
        effects: rifleHitEffects,
        beforeCalls: afterHostileProjectileCalls,
        afterCalls: afterRifleHitCalls,
        addedDrawCalls: afterRifleHitCalls - afterHostileProjectileCalls,
        beforeGeometries: afterHostileProjectileGeometries,
        afterGeometries: afterRifleHitGeometries,
        addedGeometries: afterRifleHitGeometries - afterHostileProjectileGeometries,
        beforeTriangles: afterHostileProjectileTriangles,
        afterTriangles: afterRifleHitTriangles,
        addedTriangles: afterRifleHitTriangles - afterHostileProjectileTriangles,
      },
      bladeHit: {
        effects: 1,
        beforeCalls: afterRifleHitCalls,
        afterCalls: afterBladeHitCalls,
        addedDrawCalls: afterBladeHitCalls - afterRifleHitCalls,
        beforeGeometries: afterRifleHitGeometries,
        afterGeometries: afterBladeHitGeometries,
        addedGeometries: afterBladeHitGeometries - afterRifleHitGeometries,
        beforeTriangles: afterRifleHitTriangles,
        afterTriangles: afterBladeHitTriangles,
        addedTriangles: afterBladeHitTriangles - afterRifleHitTriangles,
      },
    }
  }

  private readonly frame = (timestamp: number): void => {
    if (this.disposed) return
    const frameDelta = Math.max(0, (timestamp - this.previousTimestamp) / 1000)
    const simulationDelta = Math.min(0.1, frameDelta)
    this.previousTimestamp = timestamp
    if (this.phase === 'playing') {
      this.frameTimes.push(frameDelta)
      if (this.frameTimes.length > 180) this.frameTimes.shift()
    }
    // Hit-stop: the simulation holds, the renderer does not. A blade landing
    // freezes the WORLD for ~0.09s while the camera stays live, which is what
    // separates "a number went down" from "I felt that connect". The FX
    // freeze is the same mechanism with no timer, used by capture tooling.
    if (this.hitStopRemaining > 0 || this.fxFrozen) {
      this.hitStopRemaining = Math.max(0, this.hitStopRemaining - frameDelta)
      this.accumulator = 0
      this.updateHud()
      this.render(simulationDelta)
      this.animationFrame = requestAnimationFrame(this.frame)
      return
    }
    this.accumulator = Math.min(this.accumulator + simulationDelta, 0.2)
    while (this.accumulator >= BALANCE.fixedStep) {
      this.update(BALANCE.fixedStep)
      this.accumulator -= BALANCE.fixedStep
    }
    this.updateHud()
    this.render(simulationDelta)
    this.animationFrame = requestAnimationFrame(this.frame)
  }

  private triggerHitStop(seconds: number): void {
    if (seconds <= 0) return
    if (seconds > this.hitStopRemaining) {
      this.hitStopRemaining = seconds
      this.hitStopsTriggered += 1
    }
  }

  private update(deltaSeconds: number): void {
    if (this.phase === 'paused') return
    const worldTime = performance.now() / 1000
    this.arena.update(worldTime)
    this.effects.update(deltaSeconds)
    this.updateMissiles(deltaSeconds)
    this.updateHostileProjectiles(deltaSeconds)

    if (this.phase !== 'playing') {
      return
    }

    this.elapsedSeconds += deltaSeconds
    this.updateAim(deltaSeconds)
    const target = this.currentTarget()
    const playerFrame = this.player.update(
      deltaSeconds,
      worldTime,
      this.input,
      this.cameraYaw,
      this.targetVisible ? target?.aimPoint ?? null : null,
    )
    this.currentSpeed = playerFrame.planarSpeed
    this.boosting = playerFrame.boosting
    this.assaultBoosting = playerFrame.assaultBoosting
    this.hud.setBoost(playerFrame.boosting, playerFrame.assaultBoosting)
    this.audio.updateEngine(playerFrame.planarSpeed, playerFrame.boosting)
    if (playerFrame.quickBoosted) {
      this.cameraShake = Math.max(this.cameraShake, 0.28)
      this.hud.setToast('QUICK VECTOR', 'info', 520)
    }
    this.dustTimer -= deltaSeconds
    if (playerFrame.planarSpeed > 4 && !playerFrame.airborne && this.dustTimer <= 0) {
      this.effects.dust(
        this.player.position,
        this.player.velocity.clone().multiplyScalar(-1),
        playerFrame.boosting ? 1.4 : 0.8,
      )
      this.dustTimer = playerFrame.boosting ? 0.045 : 0.11
    }

    this.updateCooldowns(deltaSeconds)
    this.handleWeapons()
    this.updateEnemies(deltaSeconds, worldTime)
    this.updateMission(deltaSeconds)
    this.checkMissionState()
  }

  private updateAim(deltaSeconds: number): void {
    const look = this.input.consumeLook()
    this.cameraYaw -= look.x * 0.00175
    this.cameraPitch = clamp(this.cameraPitch - look.y * 0.00145, -0.08, 0.62)

    if (this.input.wasPressed('Tab')) this.cycleTarget()
    const target = this.currentTarget()
    this.targetVisible = target
      ? this.arena.hasLineOfSight(this.player.aimPoint, target.aimPoint, 0.12)
      : false
    if (target && this.targetVisible) {
      this.lockProgress = Math.min(1, this.lockProgress + deltaSeconds * 2.2)
      const direction = target.position.clone().sub(this.player.position)
      const targetYaw = Math.atan2(direction.x, -direction.z)
      const turn = Math.atan2(Math.sin(targetYaw - this.cameraYaw), Math.cos(targetYaw - this.cameraYaw))
      this.cameraYaw += turn * (1 - Math.exp(-1.45 * deltaSeconds))
    } else {
      this.lockProgress = Math.max(0, this.lockProgress - deltaSeconds * 4)
      if (target && this.lockProgress === 0) this.targetId = null
      if (!this.targetId && this.enemies.some((enemy) => enemy.alive)) this.acquireTarget()
    }
  }

  private updateCooldowns(deltaSeconds: number): void {
    this.rifleCooldown = Math.max(0, this.rifleCooldown - deltaSeconds)
    this.missileCooldown = Math.max(0, this.missileCooldown - deltaSeconds)
    this.bladeCooldown = Math.max(0, this.bladeCooldown - deltaSeconds)
    if (this.reloadTimer > 0) {
      this.reloadTimer = Math.max(0, this.reloadTimer - deltaSeconds)
      if (this.reloadTimer === 0) this.finishReload()
    }
    if ((this.input.wasPressed('KeyR') || this.rifleAmmo === 0) && this.rifleReserve > 0) {
      this.beginReload()
    }
  }

  private handleWeapons(): void {
    const rifleRequested = this.input.isMouseDown(0) || this.input.isDown('KeyJ')
    if (rifleRequested && this.rifleCooldown === 0 && this.reloadTimer === 0) this.fireRifle()

    const missileRequested = this.input.wasMousePressed(2) || this.input.wasPressed('KeyK')
    if (missileRequested && this.missileCooldown === 0) this.fireMissileSalvo()

    const bladeRequested = this.input.wasPressed('KeyF') || this.input.wasPressed('KeyL')
    if (bladeRequested && this.bladeCooldown === 0) this.fireBlade()
  }

  private fireRifle(): void {
    if (this.rifleAmmo <= 0) {
      this.beginReload()
      return
    }
    this.rifleAmmo -= 1
    this.rifleCooldown = BALANCE.rifle.cooldown
    this.player.rig.root.updateMatrixWorld(true)
    const origin = this.player.rig.weaponMuzzle.getWorldPosition(new THREE.Vector3())
    const target = this.currentTarget()
    const targetPoint = target?.aimPoint ?? null
    const targetInRange = targetPoint !== null && origin.distanceTo(targetPoint) <= BALANCE.rifle.range
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.player.rig.root.quaternion)
    const intendedEndpoint = targetInRange
      ? targetPoint
      : origin.clone().addScaledVector(forward, BALANCE.rifle.range)
    const trace = this.arena.traceLine(origin, intendedEndpoint, 0.06, 0.04)
    const endpoint = trace.point
    if (target && targetInRange && !trace.blocked) {
      this.rifleHits += 1
      this.damageEnemy(target, BALANCE.rifle.damage, BALANCE.rifle.impact, endpoint, 'rifle')
    } else if (trace.blocked) {
      // Cover strike: the dull spark and dull thud, deliberately unlike any
      // scoring hit, so "I hit the wall" is readable without looking up.
      this.effects.impact(endpoint)
      this.audio.impact()
    }
    this.effects.muzzleFlash(origin, forward)
    this.effects.tracer(origin, endpoint)
    this.audio.rifle()
    this.player.kickRecoil(0.24)
    this.cameraShake = Math.max(this.cameraShake, 0.11)
    if (this.rifleAmmo === 0 && this.rifleReserve > 0) this.beginReload()
  }

  private fireMissileSalvo(): void {
    const target = this.currentTarget()
    if (!target || !this.targetVisible || this.lockProgress < 0.72) {
      this.hud.setToast('MISSILE LOCK REQUIRED', 'warning', 1000)
      return
    }
    if (this.missileAmmo <= 0) {
      this.hud.setToast('MISSILE CELLS EMPTY', 'warning', 1100)
      return
    }
    const salvo = Math.min(BALANCE.missile.salvo, this.missileAmmo)
    this.missileAmmo -= salvo
    this.missileCooldown = BALANCE.missile.cooldown
    this.player.rig.root.updateMatrixWorld(true)
    for (let index = 0; index < salvo; index += 1) {
      const port = this.player.rig.missilePorts[index % this.player.rig.missilePorts.length]
      if (!port) continue
      const origin = port.getWorldPosition(new THREE.Vector3())
      const side = index % 2 === 0 ? -1 : 1
      const velocity = new THREE.Vector3(side * (3.5 + index), 7 + index * 0.7, -9)
        .applyQuaternion(this.player.rig.root.quaternion)
        .normalize()
        .multiplyScalar(BALANCE.missile.speed * 0.62)
      this.missiles.push({
        group: this.createMissileMesh(origin),
        velocity,
        targetId: target.id,
        life: BALANCE.missile.life,
      })
    }
    this.audio.missile()
    this.player.kickRecoil(0.38)
    this.hud.setToast(`SALVO // ${salvo} TRACKS`, 'success', 500)
  }

  private fireBlade(): void {
    this.bladeCooldown = BALANCE.blade.cooldown
    const target = this.currentTarget()
    this.effects.slash(this.player.position, this.player.rig.root.rotation.y)
    this.audio.melee()
    this.cameraShake = Math.max(this.cameraShake, 0.34)
    if (
      target &&
      target.position.distanceTo(this.player.position) <= BALANCE.blade.range &&
      this.arena.hasLineOfSight(this.player.aimPoint, target.aimPoint, 0.35)
    ) {
      this.damageEnemy(target, BALANCE.blade.damage, BALANCE.blade.impact, target.aimPoint, 'blade')
    } else {
      this.hud.setToast('BLADE // NO CONTACT', 'warning', 620)
    }
  }

  private beginReload(): void {
    if (this.reloadTimer > 0 || this.rifleAmmo >= 36 || this.rifleReserve <= 0) return
    this.reloadTimer = 1.2
    this.hud.setToast('MAGAZINE CYCLING', 'info', 900)
  }

  private finishReload(): void {
    const needed = 36 - this.rifleAmmo
    const amount = Math.min(needed, this.rifleReserve)
    this.rifleAmmo += amount
    this.rifleReserve -= amount
  }

  private updateEnemies(deltaSeconds: number, worldTime: number): void {
    for (const enemy of this.enemies) {
      const shot = enemy.update(deltaSeconds, worldTime, this.player.position)
      if (shot) this.spawnHostileProjectile(shot.origin, shot.direction, shot.damageScale)
    }
  }

  private updateMission(deltaSeconds: number): void {
    const enemiesAlive = this.enemies.filter((enemy) => enemy.alive).length
    const transition = this.mission.update(deltaSeconds, enemiesAlive)
    if (transition.enteredIntermission) {
      this.targetId = null
      this.targetVisible = false
      this.lockProgress = 0
      this.hud.setToast('WAVE CLEAR // REINFORCEMENTS INBOUND', 'warning', 1400)
      this.audio.alarm()
    }
    if (transition.spawnWave) {
      this.spawnWave(transition.spawnWave)
      const state = this.mission.snapshot(transition.spawnWave.units.length)
      this.hud.setToast(
        `STAGE ${state.stage}/${state.totalStages} // HOSTILE SIGNALS ACQUIRED`,
        'warning',
        1500,
      )
      this.audio.alarm()
      this.acquireTarget()
    }
  }

  private spawnHostileProjectile(origin: THREE.Vector3, direction: THREE.Vector3, damageScale: number): void {
    this.hostileProjectiles.push({
      position: origin.clone(),
      velocity: direction.clone().multiplyScalar(BALANCE.enemy.projectileSpeed),
      life: 4.5,
      damageScale,
    })
    this.hostileProjectileBatch.sync(this.hostileProjectiles)
  }

  private updateHostileProjectiles(deltaSeconds: number): void {
    for (let index = this.hostileProjectiles.length - 1; index >= 0; index -= 1) {
      const projectile = this.hostileProjectiles[index]
      if (!projectile) continue
      const previous = projectile.position.clone()
      const intended = projectile.position.clone().addScaledVector(projectile.velocity, deltaSeconds)
      const trace = this.arena.traceLine(previous, intended, 0.2)
      projectile.position.copy(trace.point)
      projectile.life -= deltaSeconds
      const hitDistance = distanceToSegment(this.player.aimPoint, previous, projectile.position)
      if (this.phase === 'playing' && hitDistance < 2.25) {
        // Incoming fire used to be one fixed picture: identical spark, one
        // thud, camera shake pinned at 0.48 no matter what landed. Everything
        // below now scales with the damage that was actually applied, and the
        // HUD prints the figure, so "what did that cost me" has an answer.
        const wasOverloaded = this.player.stagger.overloadedFor > 0
        const baseDamage = BALANCE.enemy.projectileDamage * projectile.damageScale
        const outcome = this.player.applyDamage(
          baseDamage,
          BALANCE.enemy.projectileImpact * projectile.damageScale,
          previous,
        )
        const applied = wasOverloaded ? baseDamage * 1.45 : baseDamage
        const heavy = applied >= 500
        const shake = incomingShake(applied)
        this.effects.impact(projectile.position, true)
        if (heavy) this.effects.hit({ weapon: 'missile', tier: 'solid', direct: false }, projectile.position)
        this.audio.playerHit(heavy, wasOverloaded)
        this.hud.pulseDamage(incomingPulse(applied))
        this.hud.markHitDirection(this.player.lastHitYaw)
        this.hud.showIncomingDamage(Math.floor(applied), wasOverloaded)
        this.cameraShake = Math.max(this.cameraShake, shake)
        this.lastPlayerHit = {
          damage: Math.floor(applied),
          direct: wasOverloaded,
          shake: Number(shake.toFixed(3)),
          atSeconds: Number(this.elapsedSeconds.toFixed(2)),
        }
        this.springCaptureTrap('playerHit')
        if (outcome.overloaded) {
          // Our own ACS breaking gets the same theatre the enemy's does —
          // it is the single most important thing on screen when it happens.
          this.effects.staggerBreak(this.player.aimPoint)
          this.audio.staggerBreak()
          this.hud.setToast('ACS OVERLOAD // DIRECT HIT RISK', 'warning', 1700)
          this.cameraShake = Math.max(this.cameraShake, 0.75)
          this.triggerHitStop(0.1)
        }
        this.removeHostileProjectile(index)
      } else if (trace.blocked) {
        this.effects.impact(projectile.position, true)
        this.removeHostileProjectile(index)
      } else if (projectile.life <= 0 || projectile.position.length() > 260) {
        this.removeHostileProjectile(index)
      }
    }
    this.hostileProjectileBatch.sync(this.hostileProjectiles)
  }

  private createMissileMesh(position: THREE.Vector3): THREE.Group {
    const group = new THREE.Group()
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.13, 1.05, 7),
      new THREE.MeshStandardMaterial({ color: 0x8a8f8c, metalness: 0.8, roughness: 0.35 }),
    )
    group.add(body)
    const tip = new THREE.Mesh(
      new THREE.ConeGeometry(0.13, 0.38, 7),
      new THREE.MeshStandardMaterial({ color: 0x2b3234, metalness: 0.8, roughness: 0.4 }),
    )
    tip.position.y = 0.7
    group.add(tip)
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.11, 1.15, 8, 1, true),
      new THREE.MeshBasicMaterial({
        color: COLORS.hot,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    flame.position.y = -1.05
    flame.rotation.z = Math.PI
    group.add(flame)
    group.position.copy(position)
    group.scale.setScalar(1.25)
    this.scene.add(group)
    return group
  }

  private updateMissiles(deltaSeconds: number): void {
    for (let index = this.missiles.length - 1; index >= 0; index -= 1) {
      const missile = this.missiles[index]
      if (!missile) continue
      missile.life -= deltaSeconds
      const target = this.enemies.find((enemy) => enemy.id === missile.targetId && enemy.alive)
      if (target) {
        const desired = target.aimPoint.sub(missile.group.position).normalize().multiplyScalar(BALANCE.missile.speed)
        missile.velocity.lerp(desired, 1 - Math.exp(-BALANCE.missile.turnRate * deltaSeconds))
      }
      const previous = missile.group.position.clone()
      const intended = previous.clone().addScaledVector(missile.velocity, deltaSeconds)
      const trace = this.arena.traceLine(previous, intended, 0.2)
      missile.group.position.copy(trace.point)
      if (missile.velocity.lengthSq() > 0.01) {
        missile.group.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          missile.velocity.clone().normalize(),
        )
      }
      if (trace.blocked) {
        this.effects.explosion(missile.group.position, 0.34)
        this.removeMissile(index)
      } else if (target && missile.group.position.distanceTo(target.aimPoint) < 2.35) {
        this.missileHits += 1
        this.damageEnemy(target, BALANCE.missile.damage, BALANCE.missile.impact, target.aimPoint, 'missile')
        this.removeMissile(index)
      } else if (missile.life <= 0) {
        this.effects.explosion(missile.group.position, 0.34)
        this.removeMissile(index)
      }
    }
  }

  /**
   * The request's core fix lives here. This method previously read only
   * `destroyed`/`overloaded`/`phaseShifted` from the outcome and called
   * `effects.impact(position)` — position alone — so a 285-damage rifle graze
   * and a 2,887-damage blade direct hit were byte-identical on screen. It now
   * forwards weapon, applied damage and the direct flag to every output the
   * player has: effects, audio, HUD read-out, camera, and time itself.
   *
   * `weapon: 'system'` (scripted/test damage) keeps the outcome-driven
   * theatre — break, purge, kill — but skips per-hit FX and numbers, so
   * scripted mission captures don't show 66,000-damage floaters.
   */
  private damageEnemy(
    enemy: EnemyUnit,
    damage: number,
    impact: number,
    position: THREE.Vector3,
    weapon: HitWeapon = 'system',
  ): void {
    if (!enemy.alive) return
    const outcome = enemy.applyHit(damage, impact, this.player.position)
    const direct = outcome.damageApplied > damage + 1e-6
    const tier = hitTier(outcome.damageApplied)
    let stop = 0

    if (weapon !== 'system') {
      this.effects.hit({ weapon, tier, direct }, position, this.player.rig.root.rotation.y)
      this.audio.weaponHit(weapon as 'rifle' | 'missile' | 'blade', direct)
      const screen = this.projectToScreen(position)
      // Rapid fire folds into one growing per-target counter (windowed in
      // feedback.ts); the display tier follows the running total, so a
      // sustained rifle stream escalates the read-out the way it should.
      const counter = this.foldDamageCounter(enemy.id, outcome.damageApplied, direct)
      // Floor, not round: the project's documents quote the blade direct hit
      // as 1,750 × 1.65 = 2,887 (the exact figure is 2,887.5), so the screen
      // and the telemetry agree with the paper.
      this.hud.spawnDamage(screen.x, screen.y, Math.floor(counter.total), counter.tier, counter.direct, enemy.id)
      this.hud.showHitMarker(outcome.destroyed ? 'kill' : direct ? 'direct' : 'hit')
      this.lastHit = {
        weapon,
        damage: Math.floor(outcome.damageApplied),
        direct,
        tier,
        atSeconds: Number(this.elapsedSeconds.toFixed(2)),
      }
      stop = weaponHitStopSeconds(weapon, direct)
      this.fovKick = Math.max(this.fovKick, fovKickDegrees({ weapon, direct }))
      if (weapon === 'blade') this.cameraShake = Math.max(this.cameraShake, direct ? 0.4 : 0.3)
      this.springCaptureTrap(direct ? 'direct' : 'hit', weapon)
      if (direct) this.springCaptureTrap('hit', weapon)
    }

    if (outcome.overloaded) {
      // The break instant: rings + pillar at the enemy, a mix duck under a
      // falling zap, a panel flash, and the longest stop in the game. The
      // three ACS states (filling / OVERLOAD / recovering) are handled as
      // persistent panel state in updateHud; this is only the transition.
      this.effects.staggerBreak(enemy.aimPoint)
      this.audio.staggerBreak()
      this.hud.flashStaggerBreak()
      this.hud.setToast('TARGET ACS OVERLOAD // DIRECT HIT', 'success', 1250)
      stop = Math.max(stop, STAGGER_BREAK_STOP_SECONDS)
      this.cameraShake = Math.max(this.cameraShake, 0.3)
      this.fovKick = Math.max(this.fovKick, 5)
      this.springCaptureTrap('staggerBreak')
    }
    if (outcome.phaseShifted) {
      // Armour purge: the command unit sheds plate and comes forward. Flagged
      // loudly because its firing cadence more than doubles at this point.
      this.effects.explosion(enemy.aimPoint, 1.1)
      this.audio.alarm()
      this.hud.setToast('COMMAND UNIT // ARMOUR PURGE — CADENCE RISING', 'warning', 2400)
      this.cameraShake = Math.max(this.cameraShake, 0.42)
    }
    if (outcome.destroyed) {
      const heavy = enemy.enemyClass === 'bulwark'
      this.effects.killBlast(enemy.aimPoint, heavy)
      this.audio.kill(heavy)
      enemy.rig.root.visible = false
      this.score += heavy ? 4_000 : 2_000
      stop = Math.max(stop, weapon === 'system' ? 0 : killHitStopSeconds(heavy))
      this.cameraShake = Math.max(this.cameraShake, heavy ? 0.5 : 0.34)
      this.springCaptureTrap('kill', weapon)
      if (this.targetId === enemy.id) {
        this.targetId = null
        this.lockProgress = 0
        this.acquireTarget()
      }
    }
    this.triggerHitStop(stop)
  }

  /** World → normalized screen position for HUD damage read-outs. */
  private projectToScreen(position: THREE.Vector3): { x: number; y: number } {
    const projected = position.clone().project(this.camera)
    return { x: (projected.x + 1) / 2, y: (1 - projected.y) / 2 }
  }

  /** Running totals for the per-target cumulative damage read-out. */
  private readonly damageCounters = new Map<string, DamageCounterState>()

  private foldDamageCounter(enemyId: string, damageApplied: number, direct: boolean): DamageCounterState {
    const next = accumulateDamage(
      this.damageCounters.get(enemyId) ?? null,
      damageApplied,
      direct,
      this.elapsedSeconds,
    )
    this.damageCounters.set(enemyId, next)
    return next
  }

  private acquireTarget(): void {
    const forward = new THREE.Vector3(Math.sin(this.cameraYaw), 0, -Math.cos(this.cameraYaw))
    const candidates = this.enemies
      .filter(
        (enemy) =>
          enemy.alive &&
          this.arena.hasLineOfSight(this.player.aimPoint, enemy.aimPoint, 0.12),
      )
      .map((enemy) => {
        const delta = enemy.position.clone().sub(this.player.position)
        const distance = delta.length()
        const alignment = distance > 0 ? forward.dot(delta.normalize()) : 1
        return { id: enemy.id, distance, alignment, alive: enemy.alive }
      })
    this.targetId = chooseLockTarget(candidates, 150, -0.65)
    this.targetVisible = this.targetId !== null
    this.lockProgress = 0
  }

  private cycleTarget(): void {
    const alive = this.enemies.filter(
      (enemy) =>
        enemy.alive &&
        this.arena.hasLineOfSight(this.player.aimPoint, enemy.aimPoint, 0.12),
    )
    if (alive.length === 0) {
      this.targetId = null
      this.targetVisible = false
      return
    }
    const currentIndex = alive.findIndex((enemy) => enemy.id === this.targetId)
    this.targetId = alive[(currentIndex + 1) % alive.length]?.id ?? alive[0]?.id ?? null
    this.targetVisible = this.targetId !== null
    this.lockProgress = 0
    this.hud.setToast('FCS TRACK SWITCH', 'info', 600)
  }

  private currentTarget(): EnemyUnit | null {
    return this.enemies.find((enemy) => enemy.id === this.targetId && enemy.alive) ?? null
  }

  private checkMissionState(): void {
    const enemiesAlive = this.enemies.filter((enemy) => enemy.alive).length
    const missionState = this.mission.snapshot(enemiesAlive)
    const outcome = evaluateMission(
      this.player.alive,
      missionState.phase === 'complete' ? 0 : 1,
    )
    if (outcome === 'defeat' && this.phase === 'playing') {
      this.phase = 'defeat'
      this.hud.setBoost(false, false)
      this.audio.kill(true)
      this.effects.killBlast(this.player.aimPoint, true)
      this.player.rig.root.visible = false
      this.cameraShake = Math.max(this.cameraShake, 0.9)
      this.hud.showResult(
        {
          victory: false,
          title: 'FRAME LOST',
          summary: '戦闘記録は保存されました。再展開して敵の射線をクイックブーストで外してください。',
          score: this.score,
          elapsedSeconds: this.elapsedSeconds,
        },
        () => window.location.reload(),
      )
      if (document.pointerLockElement) void document.exitPointerLock()
      return
    }
    if (outcome === 'victory' && this.phase === 'playing') {
      this.phase = 'victory'
      this.hud.setBoost(false, false)
      this.score += Math.max(0, Math.round(6_000 - this.elapsedSeconds * 20))
      this.hud.showResult(
        {
          victory: true,
          title: 'VEIL SECURED',
          summary: '敵指揮信号を全て沈黙させました。IRON VEIL は独自戦闘データを回収しました。',
          score: this.score,
          elapsedSeconds: this.elapsedSeconds,
        },
        () => window.location.reload(),
      )
      if (document.pointerLockElement) void document.exitPointerLock()
    }
  }

  private updateHud(): void {
    const target = this.currentTarget()
    const targetDistance = target ? target.position.distanceTo(this.player.position) : 0
    const enemiesAlive = this.enemies.filter((enemy) => enemy.alive).length
    const missionState = this.mission.snapshot(enemiesAlive)
    const projectedTarget = target ? target.aimPoint.project(this.camera) : null
    const hudState: HUDState = {
      armor: this.player.armor,
      maxArmor: BALANCE.player.maxAp,
      energy: this.player.energy.value,
      maxEnergy: BALANCE.player.maxEnergy,
      stagger: this.player.stagger.value,
      maxStagger: BALANCE.player.maxStagger,
      weapon: {
        name: 'VX-9 BURST RIFLE',
        ammo: this.rifleAmmo,
        reserve: this.rifleReserve,
        status: this.reloadTimer > 0 ? 'cycling' : this.rifleAmmo === 0 ? 'empty' : 'ready',
        heat: this.rifleCooldown / BALANCE.rifle.cooldown,
        rifleCooldown: this.rifleCooldown / BALANCE.rifle.cooldown,
        missileCooldown: this.missileCooldown / BALANCE.missile.cooldown,
        bladeCooldown: this.bladeCooldown / BALANCE.blade.cooldown,
      },
      missiles: this.missileAmmo,
      lockProgress: this.lockProgress,
      target: target
        ? {
            name: target.enemyClass === 'bulwark' ? 'MORRIGAN // BULWARK' : 'MORRIGAN // SKIRMISHER',
            distance: targetDistance,
            armor: target.armor,
            maxArmor: target.maximumArmor,
            stagger: target.stagger.value,
            maxStagger: target.maximumStagger,
            locked: this.targetVisible && this.lockProgress >= 0.995,
            // ACS overload and command phase ride to the panel so the ×1.65
            // window and the cadence change are persistent state, not a toast
            // that scrolled away 2.4 seconds after the fact.
            overloaded: target.stagger.overloadedFor > 0,
            phase: target.combatPhase,
            threat: target.enemyClass === 'bulwark' ? 'COMMAND UNIT' : 'HOSTILE',
            screenX: projectedTarget ? (projectedTarget.x + 1) / 2 : undefined,
            screenY: projectedTarget ? (1 - projectedTarget.y) / 2 : undefined,
          }
        : null,
      objective: {
        title:
          missionState.phase === 'intermission'
            ? 'REINFORCEMENT WINDOW'
            : this.mission.currentWave.title,
        detail:
          missionState.phase === 'intermission'
            ? `STAGE ${Math.min(missionState.totalStages, missionState.stage + 1)}/${missionState.totalStages} INBOUND // ${missionState.intermissionRemaining.toFixed(1)}s`
            : missionState.phase === 'complete'
              ? 'ALL COMMAND SIGNALS SILENCED'
              : `${this.mission.currentWave.detail} // ${enemiesAlive} SIGNALS`,
        progress: missionState.progress,
      },
      elapsedSeconds: this.elapsedSeconds,
    }
    this.hud.update(hudState)
  }

  /**
   * Chase distance before obstacles are considered.
   *
   * Pulled back from 14.5/18 so the mech reads at roughly a quarter of screen
   * height instead of filling the middle of it. Exposed because the camera's
   * real invariant is "an obstacle shortens the boom", which is only testable
   * against the nominal — a fixed number in the test would quietly stop
   * proving anything the next time this is tuned.
   */
  private nominalCameraDistance(): number {
    return this.assaultBoosting ? 31 : this.boosting ? 25 : 21
  }

  private render(deltaSeconds: number): void {
    this.cameraShake = Math.max(0, this.cameraShake - deltaSeconds * 2.6)
    const forward = new THREE.Vector3(Math.sin(this.cameraYaw), 0, -Math.cos(this.cameraYaw))
    const right = new THREE.Vector3(Math.cos(this.cameraYaw), 0, Math.sin(this.cameraYaw))
    const distance = this.nominalCameraDistance()
    const desired = this.player.position
      .clone()
      .addScaledVector(forward, -distance)
      .addScaledVector(right, 2.2)
      .add(new THREE.Vector3(0, 9.6 + this.cameraPitch * 9, 0))
    const smoothing = 1 - Math.exp(-6.5 * Math.max(0.001, deltaSeconds))
    const cameraFocus = this.player.aimPoint
    const clippedDesired = this.arena.traceLine(cameraFocus, desired, 0.7, 0.3)
    const candidate = this.camera.position.clone().lerp(clippedDesired.point, smoothing)
    const target = this.currentTarget()
    const lookAt = this.player.aimPoint
    if (target && this.targetVisible) lookAt.lerp(target.aimPoint, 0.13)
    const time = performance.now() / 1000
    if (this.cameraShake > 0) {
      const amplitude = this.cameraShake * this.cameraShake
      candidate.x += Math.sin(time * 91) * amplitude
      candidate.y += Math.cos(time * 73) * amplitude * 0.7
      lookAt.x += Math.sin(time * 63) * amplitude * 0.35
    }
    const safeCamera = this.arena.traceLine(cameraFocus, candidate, 0.7, 0.24)
    this.camera.position.copy(safeCamera.point)
    this.cameraObstructed = clippedDesired.blocked || safeCamera.blocked
    this.camera.lookAt(lookAt)
    // fovKick: a brief TIGHTEN on heavy connections (blade, stagger break).
    // Decays at 22°/s so it reads as a punch, not a zoom.
    this.fovKick = Math.max(0, this.fovKick - deltaSeconds * 22)
    const desiredFov = (this.assaultBoosting ? 82 : this.boosting ? 67 : 58) - this.fovKick
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, desiredFov, 0.1)
    this.camera.updateProjectionMatrix()
    this.renderer.info.reset()
    this.composer.render(deltaSeconds)
  }

  private spawnWave(wave: MissionWaveDefinition): void {
    for (const unit of wave.units) {
      this.enemies.push(
        new EnemyUnit(
          this.scene,
          unit.id,
          unit.enemyClass,
          new THREE.Vector3(...unit.position),
          unit.phaseOffset,
          this.arena,
        ),
      )
    }
  }

  private removeHostileProjectile(index: number): void {
    const projectile = this.hostileProjectiles[index]
    if (!projectile) return
    this.hostileProjectiles.splice(index, 1)
  }

  private removeMissile(index: number): void {
    const missile = this.missiles[index]
    if (!missile) return
    this.scene.remove(missile.group)
    missile.group.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return
      object.geometry.dispose()
      if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose())
      else object.material.dispose()
    })
    this.missiles.splice(index, 1)
  }

  private readonly resize = (): void => {
    const width = Math.max(1, window.innerWidth)
    const height = Math.max(1, window.innerHeight)
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height, false)
    this.composer.setSize(width, height)
  }

  private readonly capturePointer = (): void => {
    if (this.phase === 'playing') this.input.requestPointerLock()
  }

  private readonly handlePointerLockChange = (): void => {
    if (document.pointerLockElement === this.canvas) {
      if (this.phase !== 'playing') {
        this.pointerWasCaptured = false
        void document.exitPointerLock()
        return
      }
      this.pointerWasCaptured = true
      return
    }
    const lostCapturedPointer = this.pointerWasCaptured
    this.pointerWasCaptured = false
    if (lostCapturedPointer && this.phase === 'playing') this.pause()
  }

  private readonly handleGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Escape' || this.phase !== 'playing') return
    event.preventDefault()
    this.pause()
  }

  private readonly dispose = (): void => {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.animationFrame)
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('keydown', this.handleGlobalKeyDown)
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange)
    this.canvas.removeEventListener('click', this.capturePointer)
    this.input.dispose()
    this.hud.dispose()
    this.audio.dispose()
    this.effects.dispose()
    this.hostileProjectileBatch.dispose()
    this.composer.dispose()
    this.renderer.dispose()
  }
}

function distanceToSegment(point: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): number {
  const segment = end.clone().sub(start)
  const lengthSquared = segment.lengthSq()
  if (lengthSquared === 0) return point.distanceTo(start)
  const t = clamp(point.clone().sub(start).dot(segment) / lengthSquared, 0, 1)
  return point.distanceTo(start.clone().addScaledVector(segment, t))
}
